import { pxToRem } from '@/Client/Styles/pxToRem';
import { DndContext, DragEndEvent, DragOverlay, DragStartEvent, KeyboardSensor, MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { createContext, ReactNode, useContext, useState } from 'react';

interface DndContextValue {
  handleDragEnd: (event: DragEndEvent) => void;
}

const DndContextValue = createContext<DndContextValue | null>(null);

export const useDndContext = () => {
  const context = useContext(DndContextValue);
  if (!context) {
    throw new Error('useDndContext must be used within a DndProvider');
  }
  return context;
};

interface DndProviderProps {
  children: ReactNode;
  onDragEnd: (event: DragEndEvent) => void;
}

export const DndProvider = ({ children, onDragEnd }: DndProviderProps) => {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [activeData, setActiveData] = useState<any>(null);

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
    setActiveData(event.active.data.current);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    if (event.active.data?.current?.type === 'photo') {
      if (typeof event.over?.data.current?.addPhoto === 'function') {
        event.over?.data.current.addPhoto(event.active.data.current.photo);
      }
    }
    setActiveId(null);
    setActiveData(null);
    onDragEnd(event);
  };

  // center the drag overlay on the cursor
  const centerToCursorModifier = ({ transform, active, activatorEvent }: {
    transform: { x: number; y: number; scaleX: number; scaleY: number };
    active: any;
    activatorEvent: null | { type: string };
  }) => {
    if (!active || !activatorEvent || !transform) return transform;

    const isTouch = activatorEvent.type === 'touchstart';
    const offsetX = (activatorEvent as any).offsetX || 0;
    const offsetY = (activatorEvent as any).offsetY || 0;

    return {
      ...transform,
      x: transform.x + (isTouch ? 40 : (offsetX - 40)),
      y: transform.y + (isTouch ? 40 : (offsetY - 40)),
    };
  };

  // Configure sensors with proper touch support
  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      distance: 8, // 8px movement required to start drag
    },
  });

  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: {
      delay: 250, // ms to hold before drag starts
      tolerance: 8, // px finger can move before aborting (increased from 5)
    },
  });

  const keyboardSensor = useSensor(KeyboardSensor);

  const sensors = useSensors(mouseSensor, touchSensor, keyboardSensor);

  return (
    <DndContext onDragStart={handleDragStart} onDragEnd={handleDragEnd} sensors={sensors}>
      <DndContextValue.Provider value={{ handleDragEnd: onDragEnd }}>
        {children}
      </DndContextValue.Provider>
      <DragOverlay dropAnimation={null} modifiers={[centerToCursorModifier]}>
        {activeId && activeData?.type === 'photo'
          ? (
            <div style={{ position: 'relative', width: '80px', height: '80px' }}>
              <div
                style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '4px',
                  overflow: 'hidden',
                  backgroundColor: 'white',
                  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                  border: '1px solid #e0e0e0',
                }}
              >
                <img
                  src={activeData.uri}
                  alt='Drag preview'
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              </div>
              {
                /* Dragging a multi-selection carries every source along (see
                FocusedPhotoGallery.tsx) — the count is the only visual cue that
                more than the one pictured photo will move. */
              }
              {activeData.sources?.length > 1
                ? (
                  <div
                    style={{
                      position: 'absolute',
                      top: '-8px',
                      right: '-8px',
                      minWidth: '20px',
                      height: '20px',
                      padding: '0 5px',
                      borderRadius: '10px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#1a1a1a',
                      color: 'white',
                      fontSize: pxToRem(11),
                      fontWeight: 700,
                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
                    }}
                  >
                    {activeData.sources.length}
                  </div>
                )
                : null}
            </div>
          )
          : null}
      </DragOverlay>
    </DndContext>
  );
};
